import { MapPin, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

export const QuietRoomCard = ({ location, onNavigate }) => {
  // Handle both MongoDB data (current_occupancy) and static data (current_occupancy)
  const capacity = location.capacity;
  const currentOccupancy = location.current_occupancy || location.currentOccupancy || 0;
  
  const occupancyPercentage = capacity
    ? (currentOccupancy / capacity) * 100
    : 0;

  const getStatusColor = () => {
    if (occupancyPercentage > 80) return { bg: "bg-red-100", text: "text-red-600", fill: "#F87171" };
    if (occupancyPercentage > 50) return { bg: "bg-yellow-100", text: "text-yellow-600", fill: "#FBBF24" };
    return { bg: "bg-green-100", text: "text-green-600", fill: "#34D399" };
  };

  const status = getStatusColor();

  const getStatusText = () => {
    if (occupancyPercentage > 80) return "Busy";
    if (occupancyPercentage > 50) return "Moderate";
    return "Available";
  };

  // Format amenities for display
  const formatAmenity = (amenity) => {
    if (typeof amenity === 'string') {
      return amenity.replace(/_/g, " ");
    }
    return amenity;
  };

  return (
    <article
      className="quiet-room-card"
      data-testid={`quiet-room-${location.id}`}
      aria-label={`${location.name} - ${getStatusText()}`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <h3
              className="font-semibold text-lg"
              style={{ fontFamily: "Lexend, sans-serif" }}
            >
              {location.name}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {location.description}
            </p>
            {location.access_instructions && (
              <p className="text-xs text-primary mt-2">
                📍 {location.access_instructions}
              </p>
            )}
          </div>
          
          <span
            className={`text-xs font-medium px-3 py-1 rounded-full ${status.bg} ${status.text}`}
            role="status"
          >
            {getStatusText()}
          </span>
        </div>

        {/* Capacity Bar */}
        {capacity && (
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground flex items-center gap-1">
                <Users size={14} />
                Live Capacity
              </span>
              <span className="font-medium">
                {currentOccupancy}/{capacity}
              </span>
            </div>
            <div
              className="capacity-bar"
              role="progressbar"
              aria-valuenow={currentOccupancy}
              aria-valuemin={0}
              aria-valuemax={capacity}
              aria-label="Current occupancy"
            >
              <div
                className="capacity-fill"
                style={{
                  width: `${occupancyPercentage}%`,
                  backgroundColor: status.fill,
                }}
              ></div>
            </div>
          </div>
        )}

        {/* Amenities */}
        {location.amenities && location.amenities.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {location.amenities.slice(0, 5).map((amenity) => (
              <span
                key={amenity}
                className="text-xs bg-secondary px-2 py-1 rounded-full"
              >
                {formatAmenity(amenity)}
              </span>
            ))}
            {location.amenities.length > 5 && (
              <span className="text-xs text-muted-foreground">
                +{location.amenities.length - 5} more
              </span>
            )}
          </div>
        )}

        {/* Action Button */}
        <Button
          className="w-full mt-4 h-12 rounded-full"
          onClick={() => onNavigate && onNavigate(location)}
          data-testid={`navigate-to-${location.id}`}
        >
          <MapPin size={18} className="mr-2" />
          Get Directions
        </Button>
      </div>
    </article>
  );
};
